import React from 'react';
import {
  Rhythmus,
  getOrdersInPeriod,
  addDays,
  getDayName,
  formatDate,
  isSunday,
  getDateOptions,
  formatDateFull,
  ScheduledOrder,
} from './dispoUtils';
import './DispoContent.css';

interface DispoTimelineProps {
  rhythmus: Rhythmus;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  onRhythmusChange: (rhythmus: Rhythmus) => void;
}

interface TimelineOrder extends ScheduledOrder {
  isPastOrder: boolean;
  timeStatus: 'past' | 'current' | 'future';
}

const DispoTimeline: React.FC<DispoTimelineProps> = ({
  rhythmus,
  selectedDate,
  onDateChange,
  onRhythmusChange,
}) => {
  const dateOptions = getDateOptions(14);
  const timelineDays = 10; // Weniger Tage = mehr Platz pro Tag

  // Heutiges Datum (nur Datum, ohne Zeit)
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Generiere Tage für den Zeitstrahl
  const days: Date[] = [];
  for (let i = 0; i < timelineDays; i++) {
    days.push(addDays(selectedDate, i));
  }

  // Prüfe ob ein Datum heute ist
  const isToday = (date: Date): boolean => {
    return date.getTime() === today.getTime();
  };

  // Normalisiere selectedDate fuer Vergleiche
  const selectedDateNormalized = new Date(selectedDate);
  selectedDateNormalized.setHours(0, 0, 0, 0);

  // Bestimme den zeitlichen Status einer Bestellung
  const getTimeStatus = (orderDate: Date): 'past' | 'current' | 'future' => {
    const orderTime = new Date(orderDate);
    orderTime.setHours(0, 0, 0, 0);

    if (orderTime.getTime() === selectedDateNormalized.getTime()) {
      return 'current';
    }
    if (orderTime.getTime() < selectedDateNormalized.getTime()) {
      return 'past';
    }
    return 'future';
  };

  // Hole alle Bestellungen die im Zeitraum starten
  const currentOrders = getOrdersInPeriod(selectedDate, timelineDays, rhythmus);

  // Hole auch Bestellungen von VOR dem Startdatum (bis 7 Tage zurück)
  const pastOrders = getOrdersInPeriod(addDays(selectedDate, -7), 7, rhythmus);
  const endDate = addDays(selectedDate, timelineDays);

  // Kombiniere beide Listen mit Markierung
  const allOrdersUnfiltered: TimelineOrder[] = [
    ...pastOrders
      .filter(order => order.deliveryDate >= selectedDate && order.deliveryDate < endDate)
      .map(order => ({
        ...order,
        isPastOrder: true,
        timeStatus: getTimeStatus(order.orderDate)
      })),
    ...currentOrders.map(order => ({
      ...order,
      isPastOrder: false,
      timeStatus: getTimeStatus(order.orderDate)
    })),
  ];

  // Filtere Bestellungen
  const pastOrders2 = allOrdersUnfiltered.filter(order => {
    if (order.timeStatus !== 'past') return false;
    const deliveryTime = new Date(order.deliveryDate);
    deliveryTime.setHours(0, 0, 0, 0);
    if (deliveryTime.getTime() === selectedDateNormalized.getTime()) {
      return false;
    }
    return true;
  });
  const currentOrder = allOrdersUnfiltered.filter(order => order.timeStatus === 'current');
  const futureOrders = allOrdersUnfiltered.filter(order => order.timeStatus === 'future');
  const firstFutureOrder = futureOrders.length > 0 ? [futureOrders[0]] : [];

  const allOrders = [...pastOrders2, ...currentOrder, ...firstFutureOrder];

  // Berechne Position im Zeitstrahl (0-basierter Index)
  const getDayIndex = (date: Date): number => {
    const diffTime = date.getTime() - selectedDate.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Filtere sichtbare Bestellungen für die Höhenberechnung
  const visibleOrders = allOrders.filter(order => {
    const endIndex = getDayIndex(order.deliveryDate);
    return endIndex >= 0 && endIndex < timelineDays;
  });

  return (
    <div className="dispo-timeline">
      <div className="timeline-header">
        <h3>Bestell- & Lieferzyklus</h3>
        <div className="timeline-controls">
          <div className="rhythmus-selector">
            <label>Rhythmus:</label>
            <select
              value={rhythmus}
              onChange={(e) => onRhythmusChange(e.target.value as Rhythmus)}
            >
              <option value="2er">2er Rhythmus</option>
              <option value="3er">3er Rhythmus</option>
            </select>
          </div>
          <div className="date-selector">
            <label>Startdatum:</label>
            <select
              value={selectedDate.toISOString()}
              onChange={(e) => onDateChange(new Date(e.target.value))}
            >
              {dateOptions.map((date) => (
                <option key={date.toISOString()} value={date.toISOString()}>
                  {formatDateFull(date)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Desktop: Timeline Container */}
      <div className="timeline-container">
        {/* Tages-Header */}
        <div className="timeline-days">
          {days.map((day, index) => (
            <div
              key={index}
              className={`timeline-day ${isSunday(day) ? 'sunday' : ''} ${isToday(day) ? 'today' : ''}`}
            >
              <span className="day-name">{getDayName(day)}</span>
              <span className="day-date">{formatDate(day)}</span>
              {isToday(day) && <span className="today-marker">Heute</span>}
            </div>
          ))}
        </div>

        {/* Bestellungs-Balken */}
        <div className="timeline-orders" style={{ minHeight: `${visibleOrders.length * 44}px` }}>
          {visibleOrders.map((order, index) => {
            const startIndex = getDayIndex(order.orderDate);
            const endIndex = getDayIndex(order.deliveryDate);

            // Für vergangene Bestellungen: Balken beginnt am Rand
            const effectiveStartIndex = order.isPastOrder ? Math.max(0, startIndex) : startIndex;
            const displayStartIndex = Math.max(0, effectiveStartIndex);

            const width = ((endIndex - displayStartIndex + 1) / timelineDays) * 100;
            const left = (displayStartIndex / timelineDays) * 100;

            return (
              <div
                key={index}
                className={`order-bar ${order.isPastOrder ? 'past-order' : ''} time-${order.timeStatus}`}
                style={{
                  left: `${left}%`,
                  width: `${Math.min(width, 100 - left)}%`,
                  top: `${index * 44}px`,
                }}
              >
                {order.isPastOrder ? (
                  <span className="delivery-label" style={{ marginLeft: 'auto' }}>
                    Liefertag {formatDate(order.deliveryDate)}
                  </span>
                ) : (
                  <>
                    <span className="order-label">
                      Bestelltag {formatDate(order.orderDate)}
                    </span>
                    <span className="delivery-label">
                      Liefertag {formatDate(order.deliveryDate)} 
                    </span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: Kompakte Linien-Timeline */}
      <div className="mobile-line-timeline">
        {visibleOrders.map((order, index) => {
          const orderDateNorm = new Date(order.orderDate);
          orderDateNorm.setHours(0, 0, 0, 0);
          const deliveryDateNorm = new Date(order.deliveryDate);
          deliveryDateNorm.setHours(0, 0, 0, 0);
          const diffDays = Math.round((deliveryDateNorm.getTime() - orderDateNorm.getTime()) / (1000 * 60 * 60 * 24));

          return (
            <div key={index} className={`mobile-order-line time-${order.timeStatus}`}>
              {/* Bestellpunkt links */}
              {!order.isPastOrder && (
                <div className="mobile-point order-point">
                  <div className="mobile-point-dot">📦</div>
                  <div className="mobile-point-label">
                    <span className="mobile-point-date">{getDayName(order.orderDate).slice(0,2)} {formatDate(order.orderDate)}</span>
                  </div>
                </div>
              )}
              
              {/* Verbindungslinie */}
              <div className="mobile-connector-line">
                <div className="mobile-connector-bar"></div>
                <span className="mobile-connector-days">{diffDays}d</span>
              </div>
              
              {/* Lieferpunkt rechts */}
              <div className="mobile-point delivery-point">
                <div className="mobile-point-dot">🚚</div>
                <div className="mobile-point-label">
                  <span className="mobile-point-date">{getDayName(order.deliveryDate).slice(0,2)} {formatDate(order.deliveryDate)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Mobile Legend */}
      <div className="mobile-legend">
        <div className="legend-item">
          <span className="legend-dot past"></span>
          <span>Vergangen</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot current"></span>
          <span>Aktuell</span>
        </div>
        <div className="legend-item">
          <span className="legend-dot future"></span>
          <span>Geplant</span>
        </div>
      </div>

      {/* Desktop Legend */}
      <div className="timeline-legend">
        <div className="legend-item">
          <span className="legend-color time-past"></span>
          <span>Vergangene Bestellung</span>
        </div>
        <div className="legend-item">
          <span className="legend-color time-current"></span>
          <span>Aktuelle Bestellung</span>
        </div>
        <div className="legend-item">
          <span className="legend-color time-future"></span>
          <span>Nächste Bestellung</span>
        </div>
      </div>
    </div>
  );
};

export default DispoTimeline;
